import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Today in IST as 'YYYY-MM-DD' — mirrors the daily-roster convention so a
// back-dated exit and the attendance month cutoff agree on "today".
function todayYmd(): string {
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

// Shared exit/deactivate confirm. Every exit entry point (account-detail Exit,
// deal Exit, member-profile Deactivate, manage-membership Remove) now asks for
// an exit DATE instead of flipping status silently — the chosen date is written
// as the membership end date, and the backend deactivates the member once their
// last active plan exits. Exit is a soft status flip; history and paid
// receipts/invoices are always preserved (never a delete).
export function ExitPlanDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  isDeal = false,
  verb = "Exit",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (exitDate: string) => void;
  isPending: boolean;
  isDeal?: boolean;
  /** CTA wording — "Exit" | "Deactivate" | "Remove". */
  verb?: string;
}) {
  const [exitDate, setExitDate] = useState(todayYmd);

  // Reset to today each time the dialog opens (so a stale back-date never
  // carries over between different rows).
  useEffect(() => {
    if (open) setExitDate(todayYmd());
  }, [open]);

  const subject = isDeal ? "this deal" : "this plan";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {verb} {subject}?
          </DialogTitle>
          <DialogDescription>
            Pick the exit date. History and any paid receipts/invoices are preserved — this never
            deletes records. The member is marked Deactivated once their last active plan exits.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-1">
          <Label htmlFor="exit-date">Exit date</Label>
          <Input
            id="exit-date"
            type="date"
            value={exitDate}
            max={todayYmd()}
            onChange={(e) => setExitDate(e.target.value)}
            className="h-9 w-[180px]"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => exitDate && onConfirm(exitDate)} disabled={isPending || !exitDate}>
            {isPending ? "Working…" : verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
